import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Card,
  CardMedia,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Add as AddIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';

const ImageManagement = ({ open, onClose, product, onImageUpdated }) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newImages, setNewImages] = useState([]);
  const [deletingImages, setDeletingImages] = useState(new Set());

  // Get existing product images from comma-separated string
  const getExistingImages = () => {
    if (!product) return [];
    
    // If product has multiple images as array, return them
    if (product.images && Array.isArray(product.images)) {
      return product.images;
    }

    
    // If product has images as comma-separated string
    console.log("🚀 ~ getExistingImages ~ product.image:", product.image)
    console.log("🚀 ~ getExistingImages ~ typeof product.image === 'string':", typeof product.image === 'string')
    if (product.image && typeof product.image === 'string') {
      
      console.log("🚀 ~ getExistingImages ~ product.image:", product.image)
      const imageUrls = product.image.split(',').filter(url => url.trim() !== '');
      console.log("🚀 ~ getExistingImages ~ imageUrls:", imageUrls)
      return imageUrls.map((url, index) => ({
        url: url,
        id: `existing_${index}`
      }));
    }
    
    return [];
  };

  const existingImages = getExistingImages();
const MAX_IMAGES = 5;
  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (open) {
      setNewImages([]);
      setError('');
      setSuccess('');
      setDeletingImages(new Set());
    }
  }, [open]);

  // Image upload with react-dropzone
const { getRootProps, getInputProps, isDragActive } = useDropzone({
  accept: {
    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
  },
  maxFiles: MAX_IMAGES,
  onDrop: (acceptedFiles) => {
    const availableSlots = MAX_IMAGES - existingImages.length - newImages.length;

    if (availableSlots <= 0) {
      setError(`You already have ${MAX_IMAGES} images. Delete one before adding more.`);
      return;
    }

    const filesToAdd = acceptedFiles.slice(0, availableSlots);
    const images = filesToAdd.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9)
    }));

    setNewImages(prev => [...prev, ...images]);
    setError('');
  }
});

  // Remove new image (before upload)
  const removeNewImage = (imageId) => {
    setNewImages(prev => {
      const updated = prev.filter(img => img.id !== imageId);
      // Revoke URL to prevent memory leaks
      const removedImage = prev.find(img => img.id === imageId);
      if (removedImage) {
        URL.revokeObjectURL(removedImage.preview);
      }
      return updated;
    });
  };

  // Delete existing image
  const deleteExistingImage = async (imageUrl) => {
    if (!product?.id) return;

    setDeletingImages(prev => new Set([...prev, imageUrl]));
    setError('');

    try {
      const formData = new FormData();
      formData.append('id', product.id);
      formData.append('image', imageUrl);

      const response = await apiService.post(
        API_ENDPOINTS.POST_add_or_delete_product_images,
        formData,
        "multipart/form-data"
      );

      if (response?.success) {
        setSuccess('Image deleted successfully!');
        setTimeout(() => {
          onImageUpdated();
          setSuccess('');
        }, 1500);
      } else {
        setError(response?.message || 'Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      setError(error.message || 'An error occurred while deleting the image');
    } finally {
      setDeletingImages(prev => {
        const updated = new Set(prev);
        updated.delete(imageUrl);
        return updated;
      });
    }
  };

  // Add new images
  const addNewImages = async () => {
    if (newImages.length === 0) {
      setError('Please select images to upload');
      return;
    }

    if (!product?.id) {
      setError('Product ID not found');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('id', product.id);

      // Add new images
      newImages.forEach(image => {
        formData.append('images', image.file);
      });

      const response = await apiService.post(
        API_ENDPOINTS.POST_add_or_delete_product_images,
        formData,
        "multipart/form-data"
      );

      if (response?.success) {
        setSuccess('Images uploaded successfully!');
        setNewImages([]);
        setTimeout(() => {
          onImageUpdated();
          setSuccess('');
        }, 500);
      } else {
        setError(response?.message || 'Failed to upload images');
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setError(error.message || 'An error occurred while uploading images');
    } finally {
      setIsLoading(false);
    }
  };

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Manage Product Images
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {product.name} • SKU: {product.sku}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={isLoading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Existing Images */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Current Images ({existingImages.length})
          </Typography>
          
          {existingImages.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              backgroundColor: alpha(theme.palette.grey[100], 0.3),
              borderRadius: 2,
              border: `1px dashed ${theme.palette.divider}`
            }}>
              <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No images uploaded for this product
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {existingImages.map((image, index) => (
                <Grid item xs={6} sm={4} md={3} key={image.id || index}>
                  <Card sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="140"
                      image={image.url}
                      alt={`Product image ${index + 1}`}
                      sx={{ objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    {/* Fallback for broken images */}
                    <Box
                      sx={{
                        display: 'none',
                        height: 140,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.grey[300], 0.5),
                        flexDirection: 'column'
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Image not found
                      </Typography>
                    </Box>
                    <Tooltip title="Delete Image">
                      <IconButton
                        size="small"
                        onClick={() => deleteExistingImage(image.url)}
                        disabled={deletingImages.has(image.url)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: alpha(theme.palette.error.main, 0.8),
                          color: 'white',
                          '&:hover': {
                            backgroundColor: theme.palette.error.main
                          }
                        }}
                      >
                        {deletingImages.has(image.url) ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Upload New Images */}
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Add New Images
          </Typography>
          
          <Box
            {...getRootProps()}
            sx={{
              border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.grey[100], 0.3),
              transition: 'all 0.3s ease',
              mb: 2,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderColor: theme.palette.primary.main
              }
            }}
          >
            <input {...getInputProps()} disabled={isLoading} />
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop images here' : 'Upload New Images'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag & drop images here, or click to select files
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Maximum 5 images • JPG, PNG, GIF, WebP
            </Typography>
          </Box>

          {/* New Images Preview */}
          {newImages.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                New Images to Upload ({newImages.length})
              </Typography>
              <Grid container spacing={2}>
                {newImages.map((image) => (
                  <Grid item xs={6} sm={4} md={3} key={image.id}>
                    <Card sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="120"
                        image={image.preview}
                        alt="New product image"
                        sx={{ objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeNewImage(image.id)}
                        disabled={isLoading}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: alpha(theme.palette.error.main, 0.8),
                          color: 'white',
                          '&:hover': {
                            backgroundColor: theme.palette.error.main
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          sx={{ textTransform: 'none' }}
        >
          Close
        </Button>
        
        {newImages.length > 0 && (
          <Button
            onClick={addNewImages}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            {isLoading ? 'Uploading...' : `Upload ${newImages.length} Image${newImages.length > 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImageManagement;